import { useCallback, useEffect, useRef, useState } from 'react';
import { keycloakApi } from '@/api/keycloak';
import type { GroupMember } from '@/api/keycloak';

interface UseGroupMembersResult {
  membersByGroup: Record<string, GroupMember[]>;
  countByGroup: Record<string, number>;
  isLoading: boolean;
  reload: () => void;
}

/**
 * Fetches members for each group directly from Keycloak. Group membership is a
 * property of the group (not exposed on the user list endpoint), so the only
 * reliable source of truth for "who is in this group" is GET /groups/:id/members.
 */
export const useGroupMembers = (
  groupIds: string[],
): UseGroupMembersResult => {
  const [membersByGroup, setMembersByGroup] = useState<
    Record<string, GroupMember[]>
  >({});
  const [isLoading, setIsLoading] = useState(false);

  const key = groupIds.join(',');
  const keyRef = useRef(key);
  keyRef.current = key;

  const load = useCallback(async () => {
    const ids = keyRef.current ? keyRef.current.split(',') : [];
    if (ids.length === 0) {
      setMembersByGroup({});
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    try {
      const results = await Promise.all(
        ids.map(async (id) => {
          try {
            const { members } = await keycloakApi.getGroupMembers(id);
            return [id, members] as const;
          } catch {
            return [id, [] as GroupMember[]] as const;
          }
        }),
      );
      setMembersByGroup(Object.fromEntries(results));
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  const countByGroup: Record<string, number> = {};
  for (const [id, members] of Object.entries(membersByGroup)) {
    countByGroup[id] = members.length;
  }

  return { membersByGroup, countByGroup, isLoading, reload: load };
};
